import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import PasswordInput from "../components/PasswordInput";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function RespondentLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { loginRespondent } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Respect ?next=<path> so deep-links like /q/:instrumentId survive login.
  const nextPath = params.get("next");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await loginRespondent(email, password);
      if (nextPath && nextPath.startsWith("/")) {
        nav(nextPath, { replace: true });
      } else if (user.role === "RESPONDENT") {
        nav("/consent");
      } else {
        nav("/admin");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container-narrow py-16">
        <div className="mb-8">
          <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight">Welcome back</h1>
          <p className="text-muted-foreground mt-3">Continue your assessment where you left off.</p>
        </div>
        <Card className="mano-card">
          <CardContent className="p-6 md:p-8 space-y-5">
            <GoogleSignInButton
              redirectPath={nextPath && nextPath.startsWith("/") ? nextPath : "/consent"}
              testId="login-google-btn" />
            <div className="relative flex items-center" data-testid="login-divider">
              <div className="flex-grow border-t border-border/60" />
              <span className="mx-3 text-[10px] uppercase tracking-wider text-muted-foreground">
                or with email
              </span>
              <div className="flex-grow border-t border-border/60" />
            </div>
            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password" />
              </div>
              <Button type="submit" size="lg" disabled={submitting}
                className="rounded-full w-full sm:w-auto px-10 mano-lift" data-testid="login-submit">
                {submitting ? "Signing in..." : "Sign in"}
              </Button>
              <div className="flex items-center justify-between text-sm text-muted-foreground pt-2 flex-wrap gap-2">
                <div>
                  New here?{" "}
                  <Link to="/register" className="text-primary underline underline-offset-4" data-testid="login-to-register-link">
                    Create an account
                  </Link>
                </div>
                <Link to="/forgot-password"
                      className="text-primary underline underline-offset-4"
                      data-testid="login-forgot-link">
                  Forgot password?
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
